import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import { useAudio } from '../Shared/hooks/useAudio';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import Scoreboard from '../Shared/components/Scoreboard/Scoreboard';
import Countdown from '../Shared/components/Countdown/Countdown';
import { useGameSocket } from '../Shared/hooks/useGameSocket';
import { GAME_THEME } from '../Shared/constants/GameTheme';

const ShrinkingTarget = () => {
    const canvasRef = useRef(null);
    const { beep, ping } = useAudio();

    // Constant Game Settings
    const TARGET_SIZES = [60, 50, 40, 30, 20]; // Fixed size per round
    const ROUND_DELAY = 3000; // 3 seconds between rounds

    // Game State Ref
    const stateRef = useRef({
        running: false,
        roundStartTime: 0,
        currentRound: 0, // 0 to 4 (Global round counter)
        targets: [], // [{x, y}]

        // Player Progress (Level 0-4)
        p1Level: 0,
        p2Level: 0,

        // versus state
        // versus state
        winner: null, // 'p1' or 'p2' or null
        nextRoundTime: 0,
        completed: false, // flag for game over handling prevents double submit
        p1Times: [], // used for solo
        countingDown: false,
        myHitTime: null, // timestamp or Infinity
        opHitTime: null, // timestamp or Infinity
        roundResolved: false, // Prevent double resolution
    });

    // UI State
    const [mode, setMode] = useState('solo'); // 'solo' or 'versus'
    const [gameState, setGameState] = useState('menu');
    const [roundDuration, setRoundDuration] = useState(999999); // Initial round time (Infinite)

    const [uiStats, setUiStats] = useState({
        round: 1, // Still useful for "Encounter #"
        p1Score: 0, // Display Level/Progress: 0/5
        p2Score: 0,
        lastTime: 0,
        lastRoundTime: 0,
        message: ''
    });
    const [highScores, setHighScores] = useState([]);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [showStartModal, setShowStartModal] = useState(true);
    const [countdownValue, setCountdownValue] = useState(0);

    const { status: netStatus, role: netRole, gameId: roomCode, hostGame, joinGame, sendAction, on } = useGameSocket('shrinking-target');
    const [joinCode, setJoinCode] = useState('');
    const [prepMode, setPrepMode] = useState('off'); // 'off', 'on'
    const [isFirstRound, setIsFirstRound] = useState(true);

    // Refs for stable access in callbacks
    const modeRef = useRef(mode);
    const netRoleRef = useRef(netRole);

    // Auto-close start modal when online game starts
    useEffect(() => {
        if (mode === 'online' && netStatus === 'playing') {
            setShowStartModal(false);
            // Also ensure we start countdown if not already involved
            // Actually, game_start usually triggers countdown logic via other means?
            // If we just joined, we wait for 'round_setup'? Or 'match_reset'?
            // Usually 'game_start' comes with 'role'.
            // If we are Host, we might need to initiate?
            // The existing flow: Host calls setupListener...
            // But visually, we need to hide the modal.

            // If we just entered 'playing' state, we might need to trigger reset/start logic if it's a fresh game?
            // Existing 'useEffect' on 'netStatus' ?? None visible here.

            // Let's just close the modal for now.
            // But we should also trigger 'requestReset' or ensure state is clean?
            // Host does that manually? 
            // In the UI, Host sees 'Reset Match' button. 
            // Guest waits.
            // If it's a NEW connection, 'UseGameSocket' handles 'game_start'.
            // Host creates game -> 'waiting' -> Opponent Joins -> 'playing'.
            // At this point, we show the sidebar but the game area is empty.
            // Host clicks "Reset Match" to start?
            // Or does it start automatically?
            // My test expects it to start automatically? "Auto-Start".
            // The Test drives 'mockStatus' to 'playing'.
            // If 'playing', the modal closes (with this fix).
            // Then what?
            // The test waits for 'round_setup'.
        }
    }, [mode, netStatus]);

    // Update Refs
    useEffect(() => {
        modeRef.current = mode;
        netRoleRef.current = netRole;
        stateRef.current.running = !showStartModal && !isCountingDown && (mode !== 'online' || netStatus === 'playing');
    }, [mode, netRole, showStartModal, isCountingDown, netStatus]);

    // Handlers moved below to ensure functions are defined
    const checkOnlineWin = () => {
        const state = stateRef.current;
        if (state.p1Level >= 5) finishGame('p1');
        else if (state.p2Level >= 5) finishGame('p2');
    };

    // Helpers
    const loadScores = useCallback(() => {
        const list = JSON.parse(localStorage.getItem("shrinkingTargetScores") || "[]");
        setHighScores(list);
    }, []);

    const saveScore = (totalTime, hitsCount) => {
        const list = JSON.parse(localStorage.getItem("shrinkingTargetScores") || "[]");
        list.push({ time: totalTime, avg: totalTime / 5, date: new Date().toISOString() });
        list.sort((a, b) => a.time - b.time); // Lower is better
        localStorage.setItem("shrinkingTargetScores", JSON.stringify(list.slice(0, 10)));
        loadScores();
    };

    const clearScores = useCallback(() => {
        localStorage.removeItem("shrinkingTargetScores");
        loadScores();
    }, [loadScores]);

    useEffect(() => {
        loadScores();
    }, [loadScores]);

    const generateRoundTarget = (width, height, isVersus) => {
        const pad = 80;
        if (isVersus) {
            const halfW = width / 2;
            return {
                x: Math.random() * (halfW - 2 * pad) + pad,
                y: Math.random() * (height - 2 * pad) + pad
            };
        } else {
            return {
                x: Math.random() * (width - 2 * pad) + pad,
                y: Math.random() * (height - 2 * pad) + pad
            };
        }
    };

    const resetGameState = useCallback(() => {
        const state = stateRef.current;
        state.running = false;
        state.currentRound = -1;
        state.p1Level = 0;
        state.p2Level = 0;
        state.winner = null;
        state.p1Times = [];
        state.completed = false;
        state.targets = [];
        state.nextRoundTime = 0;
        state.p1Target = null;
        state.p2Target = null;

        state.myHitTime = null;
        state.opHitTime = null;
        state.roundResolved = false;

        state.countingDown = false; // Reset this flag too
        setIsFirstRound(true);
        setUiStats({ round: 1, p1Score: 0, p2Score: 0, lastTime: 0, message: '' });
    }, []);

    // Start Sequence
    // -------------------------------------------------------------------------
    // CORE LOGIC FUNCTIONS (Reordered for Dependencies & Stability)
    // -------------------------------------------------------------------------

    const finishGame = useCallback((winner) => {
        const state = stateRef.current;
        state.completed = true;
        setGameState('finished');

        if (modeRef.current === 'solo') {
            const total = state.p1Times.reduce((a, b) => a + b, 0);
            saveScore(total);
            setUiStats(prev => ({ ...prev, lastTime: total }));
        } else if (modeRef.current === 'online') {
            const isMe = winner === netRoleRef.current;
            const msg = isMe ? "You Win the Match!" : "Opponent Wins the Match!";
            setUiStats(prev => ({ ...prev, message: msg }));
        } else {
            // Versus
            const msg = winner === 'p1' ? "Player 1 Wins the Race!" : "Player 2 Wins the Race!";
            setUiStats(prev => ({ ...prev, message: msg }));
        }
    }, [saveScore]);

    const startRound = useCallback(() => {
        const state = stateRef.current;
        state.running = true;
        state.winner = null;
        state.roundStartTime = performance.now();
        state.nextRoundTime = 0;
        beep(1000, 0.1);
        setGameState('playing');
        setCountdownValue(0);
        state.myHitTime = null;
        state.opHitTime = null;
        state.roundResolved = false;

        // Visual Round Counter

        setUiStats(prev => ({
            ...prev,
            round: state.currentRound + 1,
            message: ''
        }));
    }, [beep]);

    const scheduleNextRound = useCallback((delayMs, message) => {
        const state = stateRef.current;
        setIsCountingDown(true);
        state.nextRoundTime = 0; // Ensure loop countdown is disabled
        setGameState('waiting_next_round');
        if (message) setUiStats(prev => ({ ...prev, message }));
    }, []);

    const applyRoundSetup = useCallback((data) => {
        const state = stateRef.current;
        state.p1Target = data.p1Target;
        state.p2Target = data.p2Target;
        state.currentRound = data.round;

        // Check if we start immediately or if waiting for countdown
        if (!state.countingDown) {
            startRound();
        }
    }, [startRound]);

    const setupOnlineRound = useCallback(() => {
        const canvas = canvasRef.current;
        const width = canvas.width / 2;
        const pad = 80;

        // Host generates targets
        const p1Target = {
            x: Math.random() * (width - 2 * pad) + pad,
            y: Math.random() * (canvas.height - 2 * pad) + pad
        };
        const p2Target = {
            x: Math.random() * (width - 2 * pad) + pad,
            y: Math.random() * (canvas.height - 2 * pad) + pad
        };

        const state = stateRef.current;
        state.currentRound++; // Increment for ID

        const data = {
            type: 'round_setup',
            round: state.currentRound,
            p1Target,
            p2Target,
            timestamp: Date.now()
        };

        // 1. Send to Guest
        sendAction(data);

        // 2. Apply Locally (Host doesn't receive its own relay)
        applyRoundSetup(data);
    }, [sendAction, applyRoundSetup]);

    const endRound = useCallback((winner, time) => {
        const state = stateRef.current;
        const currentMode = modeRef.current;
        const currentRole = netRoleRef.current;

        state.running = false;
        state.winner = winner;

        if (currentMode === 'solo') {
            const t = winner === 'p1' ? time : roundDuration / 1000;
            state.p1Times.push(t);
            setUiStats(prev => ({ ...prev, lastRoundTime: t }));
            if (state.currentRound < 4) {
                state.currentRound++;
                state.targets = [generateRoundTarget(canvasRef.current.width, canvasRef.current.height, false)];
                scheduleNextRound(ROUND_DELAY, `Last Round: ${t.toFixed(3)}s`);
            } else {
                finishGame();
            }
        } else {
            // Versus/Online
            let msg = "No Winner!";
            const t = (winner === 'p1' ? state.p1HitTime : state.p2HitTime) || time; // Try to get actual time
            const timeStr = typeof t === 'number' && t !== Infinity ? `(${t.toFixed(3)}s)` : '';

            if (winner === 'p1') {
                state.p1Level++;
                if (currentMode === 'online') {
                    msg = currentRole === 'p1' ? `You Won! ${timeStr}` : `Opponent Won! ${timeStr}`;
                } else {
                    msg = `Player 1 Advances! ${timeStr}`;
                }
                setUiStats(prev => ({ ...prev, p1Score: state.p1Level, lastRoundTime: t }));
            } else if (winner === 'p2') {
                state.p2Level++;
                if (currentMode === 'online') {
                    msg = currentRole === 'p2' ? `You Won! ${timeStr}` : `Opponent Won! ${timeStr}`;
                } else {
                    msg = `Player 2 Advances! ${timeStr}`;
                }
                setUiStats(prev => ({ ...prev, p2Score: state.p2Level, lastRoundTime: t }));
            }
            // Tie?
            else if (winner === 'draw') {
                msg = "Draw! No Points.";
            }

            if (state.p1Level >= 5) {
                finishGame('p1');
            } else if (state.p2Level >= 5) {
                finishGame('p2');
            } else {
                if (currentMode === 'online') {
                    // Host initiates next round after delay
                    if (currentRole === 'p1') {
                        setTimeout(setupOnlineRound, ROUND_DELAY);
                    }
                    // Guest waits for round_setup

                    setUiStats(prev => ({ ...prev, message: msg }));
                } else {
                    state.currentRound++;
                    state.targets = [generateRoundTarget(canvasRef.current.width, canvasRef.current.height, true)];
                    scheduleNextRound(ROUND_DELAY, msg);
                }
            }
        }
    }, [finishGame, scheduleNextRound, setupOnlineRound, generateRoundTarget]);

    const checkRoundResolution = useCallback(() => {
        const state = stateRef.current;

        // If already resolved this round, do nothing
        if (state.roundResolved) return;

        // Check if both players have a result (time or Infinity)
        const myRes = state.myHitTime;
        const opRes = state.opHitTime;

        if (myRes !== null && opRes !== null) {
            state.roundResolved = true; // Lock
            state.running = false; // Stop game loop logic

            // Determine Winner
            let winner = null;
            if (myRes < opRes) winner = netRoleRef.current; // I was faster
            else if (opRes < myRes) winner = netRoleRef.current === 'p1' ? 'p2' : 'p1'; // Opponent faster
            else winner = 'draw'; // Tie (rare)

            // Normalize times for endRound
            if (netRoleRef.current === 'p1') {
                state.p1HitTime = myRes;
                state.p2HitTime = opRes;
            } else {
                state.p1HitTime = opRes;
                state.p2HitTime = myRes;
            }

            endRound(winner, myRes);
        } else {
            if (state.myHitTime !== null) {
                setUiStats(prev => ({ ...prev, message: "Waiting for Opponent..." }));
            }
        }
    }, [endRound]);

    // checkOnlineResult removed (replaced by checkRoundResolution)


    const startMatch = useCallback(() => {
        const state = stateRef.current;
        const currentMode = modeRef.current;
        const currentRole = netRoleRef.current;

        state.countingDown = false;
        setIsFirstRound(false);

        if (currentMode === 'online') {
            // Host Initiates Round 1
            if (currentRole === 'p1') {
                setupOnlineRound();
            } else {
                // Guest: Check if we missed the start trigger (race condition)
                if (state.p1Target && state.p2Target) {
                    startRound();
                } else {
                    setUiStats(prev => ({ ...prev, message: "Waiting for Host..." }));
                }
            }
        } else {
            // Local/Versus
            const canvas = canvasRef.current;
            const isSplit = currentMode === 'versus';
            state.currentRound = 0; // Explicitly start at 0 for Solo/Local
            state.targets = [generateRoundTarget(canvas.width, canvas.height, isSplit)];
            startRound();
        }
    }, [setupOnlineRound, startRound, generateRoundTarget]);

    const startGame = useCallback(() => {
        resetGameState();
        setShowStartModal(false);
        setIsCountingDown(true);
        setIsFirstRound(true);
        stateRef.current.countingDown = true;
    }, [resetGameState]);

    const requestReset = useCallback(() => {
        if (modeRef.current === 'online') {
            sendAction({ type: 'match_reset' });
            resetGameState();
            setIsCountingDown(true);
            stateRef.current.countingDown = true;
        } else {
            startGame();
        }
    }, [sendAction, resetGameState, startGame]);

    // Game Loop
    const loop = useCallback((now, dt) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const state = stateRef.current;
        const w = canvas.width;
        const h = canvas.height;

        // ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = GAME_THEME.BACKGROUND_COLOR;
        ctx.fillRect(0, 0, w, h);

        // Draw Split Screen Line for Versus
        if (mode === 'versus') {
            ctx.strokeStyle = '#444';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(w / 2, 0);
            ctx.lineTo(w / 2, h);
            ctx.stroke();

            // Labels
            ctx.fillStyle = '#3498db';
            ctx.font = 'bold 20px Inter';
            ctx.textAlign = 'center';
            ctx.fillText(`Player 1 (Lvl ${state.p1Level + 1})`, w / 4, 30);

            ctx.fillStyle = '#e74c3c';
            ctx.fillText(`Player 2 (Lvl ${state.p2Level + 1})`, (w / 4) * 3, 30);
        } else if (mode === 'online' && netRole) {
            // Online Split Screen UI
            ctx.strokeStyle = '#444';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(w / 2, 0);
            ctx.lineTo(w / 2, h);
            ctx.stroke();

            // Labels: P1 (Left), P2 (Right)
            ctx.fillStyle = '#3498db';
            ctx.font = 'bold 20px Inter';
            ctx.textAlign = 'center';
            ctx.fillText(netRole === 'p1' ? `YOU (Lvl ${state.p1Level})` : `Player 1 (Lvl ${state.p1Level})`, w / 4, 30);

            ctx.fillStyle = '#e74c3c';
            ctx.fillText(netRole === 'p2' ? `YOU (Lvl ${state.p2Level})` : `Player 2 (Lvl ${state.p2Level})`, (w / 4) * 3, 30);
        }

        // Handle Waiting State
        if (uiStats.message) {
            ctx.font = "24px Inter";
            ctx.fillStyle = "#aaa";
            ctx.textAlign = "center";
            ctx.fillText(uiStats.message, w / 2, h / 2 - 60);
        }

        if (state.running) {
            const elapsed = now - state.roundStartTime;

            if (elapsed < roundDuration) {
                // In Solo: Size depends on currentRound (0..4)
                // In Versus: Size depends on p1Level/p2Level (0..4)

                const pos = state.targets[0];

                if (mode === 'versus') {
                    // P1 Target
                    // Clamp level to 4 for array access just in case
                    const p1Size = TARGET_SIZES[Math.min(4, state.p1Level)];

                    ctx.beginPath();
                    ctx.arc(pos.x, pos.y, p1Size, 0, Math.PI * 2);
                    ctx.fillStyle = GAME_THEME.TARGET_FILL;
                    ctx.fill();
                    ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
                    ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
                    ctx.stroke();

                    // P2 Target
                    const p2Size = TARGET_SIZES[Math.min(4, state.p2Level)];
                    const p2x = w / 2 + pos.x;
                    const p2y = pos.y;

                    ctx.beginPath();
                    ctx.arc(p2x, p2y, p2Size, 0, Math.PI * 2);
                    ctx.fillStyle = GAME_THEME.TARGET_FILL;
                    ctx.fill();
                    ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
                    ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
                    ctx.stroke();
                } else if (mode === 'online') {
                    // Online Split Rendering
                    // P1 Target (Left Side)
                    // Only draw if target exists and NOT clicked by ME yet
                    const isMyRoleP1 = netRole === 'p1';
                    const myDone = state.myHitTime !== null;

                    if (state.p1Target) {
                        // If I am P1 and I'm done, don't draw my target
                        if (isMyRoleP1 && myDone) {
                            // draw nothing or ghost?
                        } else {
                            const p1Size = TARGET_SIZES[Math.min(4, state.p1Level)];
                            ctx.beginPath();
                            ctx.arc(state.p1Target.x, state.p1Target.y, p1Size, 0, Math.PI * 2);
                            ctx.fillStyle = GAME_THEME.TARGET_FILL;
                            ctx.fill();
                            ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
                            ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
                            ctx.stroke();
                        }
                    }

                    if (state.p2Target) {
                        const isMyRoleP2 = netRole === 'p2';
                        if (isMyRoleP2 && myDone) {
                            // draw nothing
                        } else {
                            const p2Size = TARGET_SIZES[Math.min(4, state.p2Level)];
                            const p2x = state.p2Target.x + w / 2;
                            const p2y = state.p2Target.y;

                            ctx.beginPath();
                            ctx.arc(p2x, p2y, p2Size, 0, Math.PI * 2);
                            ctx.fillStyle = GAME_THEME.TARGET_FILL;
                            ctx.fill();
                            ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
                            ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
                            ctx.stroke();
                        }
                    }
                } else {
                    // Solo (Full Screen Target)
                    // If online, use MY level size (Old logic, removed)
                    const level = state.currentRound;
                    const size = TARGET_SIZES[Math.min(4, level)];
                    ctx.beginPath();
                    ctx.arc(pos.x, pos.y, size, 0, Math.PI * 2);
                    ctx.fillStyle = GAME_THEME.TARGET_FILL;
                    ctx.fill();
                    ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
                    ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
                    ctx.stroke();
                }

                // Live Timer
                ctx.fillStyle = "white";
                ctx.font = "bold 24px Inter";
                ctx.textAlign = "center";
                ctx.fillText((elapsed / 1000).toFixed(3) + "s", w / 2, 80);

                // Always show last hit if available
                if (uiStats.lastRoundTime > 0) {
                    ctx.fillStyle = "#aaa";
                    ctx.font = "italic 18px Inter";
                    ctx.textAlign = "center";
                    ctx.fillText(`Last: ${uiStats.lastRoundTime.toFixed(3)}s`, w / 2, 110);
                }
            } else if (elapsed >= roundDuration) {
                // Time Limit Reached
                endRound('time_up', roundDuration / 1000);
            }
        }
    }, [mode, countdownValue, uiStats.message, checkRoundResolution, endRound, sendAction, netRole, uiStats.lastRoundTime, roundDuration]);

    useGameLoop(loop, true);

    // Register Handlers (Moved here to access defined functions)
    // Register Handlers
    useEffect(() => {
        on('match_reset', () => {
            // Received Match Reset
            resetGameState();
            setIsCountingDown(true);
            stateRef.current.countingDown = true;
        });

        on('round_setup', (data) => {
            applyRoundSetup(data);
        });

        on('player_action', (data) => {
            const state = stateRef.current;
            // Validate Round Match
            if (data.round === state.currentRound) {
                // Store Opponent Result
                const t = Number(data.time) || Infinity;
                state.opHitTime = t;
                checkRoundResolution(); // Check if we are done
            } else {
                console.warn(`Ignore stale action round ${data.round} vs ${state.currentRound}`);
            }
        });
    }, [on, startRound, resetGameState, checkRoundResolution]);

    const handleCanvasClick = (e) => {
        const state = stateRef.current;
        if (!state.running) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        const pos = state.targets[0];
        const elapsed = performance.now() - state.roundStartTime;

        if (mode === 'solo') {
            const size = TARGET_SIZES[state.currentRound];
            const dist = Math.hypot(mx - pos.x, my - pos.y);
            if (dist <= size) {
                ping();
                endRound('p1', elapsed / 1000);
            }
        } else if (mode === 'online') {
            // Online Split Screen Logic
            // If already hit (or waiting), ignore
            if (state.myHitTime !== null) return;

            const w = canvas.width;
            let hit = false;

            // Check based on Role
            if (netRole === 'p1') {
                if (mx < w / 2 && state.p1Target) {
                    const size = TARGET_SIZES[Math.min(4, state.p1Level)];
                    const dist = Math.hypot(mx - state.p1Target.x, my - state.p1Target.y);
                    if (dist <= size) hit = true;
                }
            } else if (netRole === 'p2') {
                if (mx >= w / 2 && state.p2Target) {
                    const size = TARGET_SIZES[Math.min(4, state.p2Level)];
                    const p2x = state.p2Target.x + w / 2;
                    const p2y = state.p2Target.y;
                    const dist = Math.hypot(mx - p2x, my - p2y);
                    if (dist <= size) hit = true;
                }
            }

            if (hit) {
                ping();
                const time = elapsed / 1000;
                state.myHitTime = time;
                sendAction({ type: 'player_action', action: 'hit', role: netRole, time: time, round: state.currentRound });
                checkRoundResolution();
                // Force re-render for text and target hide
                setUiStats(prev => ({ ...prev }));
            }

        } else {
            // Versus Split Screen Check
            if (mx < canvas.width / 2) {
                // P1 Side
                const size = TARGET_SIZES[Math.min(4, state.p1Level)];
                const dist = Math.hypot(mx - pos.x, my - pos.y);
                if (dist <= size) {
                    ping();
                    endRound('p1', elapsed / 1000);
                }
            } else {
                // P2 Side
                const size = TARGET_SIZES[Math.min(4, state.p2Level)];
                const p2x = canvas.width / 2 + pos.x;
                const p2y = pos.y;
                const dist = Math.hypot(mx - p2x, my - p2y);
                if (dist <= size) {
                    ping();
                    endRound('p2', elapsed / 1000);
                }
            }
        }
    };

    const sidebarContent = (
        <>
            <label>Mode</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="solo">Solo Time Trial</option>
                <option value="versus">2 Player Split Screen</option>
                <option value="online">Online Multiplayer</option>
            </select>

            <label>Prep Mode</label>
            <select value={prepMode} onChange={(e) => setPrepMode(e.target.value)}>
                <option value="off">Off (Fast Start)</option>
                <option value="on">On (5-8s Delay)</option>
            </select>

            {mode === 'solo' && (
                <>
                    <label style={{ display: 'block', marginTop: '10px' }}>Round Time Limit (Seconds)</label>
                    <select value={roundDuration} onChange={(e) => setRoundDuration(Number(e.target.value))}>
                        <option value={999999}>None (Infinite)</option>
                        <option value={5000}>5 Seconds</option>
                        <option value={3000}>3 Seconds</option>
                        <option value={2000}>2 Seconds</option>
                        <option value={1000}>1 Second</option>
                    </select>
                </>
            )}

            <div style={{ margin: '20px 0', borderTop: '1px solid #444', paddingTop: '10px' }}>
                {mode === 'solo' && (
                    <>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#436c8cff' }}>
                            {gameState === 'finished' ? uiStats.lastTime.toFixed(3) + 's' : '--'}
                        </div>
                        <div style={{ color: '#aaa' }}>Total Time</div>
                    </>
                )}
                {(mode === 'versus' || mode === 'online') && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#3498db', fontWeight: 'bold' }}>
                                {mode === 'online' ? (netRole === 'p1' ? 'YOU (P1)' : 'Player 1') : 'Player 1'}
                            </div>
                            <div style={{ fontSize: '32px' }}>{uiStats.p1Score} / 5</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                                {mode === 'online' ? (netRole === 'p2' ? 'YOU (P2)' : 'Player 2') : 'Player 2'}
                            </div>
                            <div style={{ fontSize: '32px' }}>{uiStats.p2Score} / 5</div>
                        </div>
                    </div>
                )}
                <div style={{ marginTop: '15px', color: '#aaa', fontSize: '14px' }}>
                    {mode === 'solo' ? `Round: ${uiStats.round} / 5` : 'First to 5 wins!'}
                </div>
            </div>

            {mode === 'online' ? (
                netStatus === 'waiting_for_opponent' ? (
                    <div style={{ textAlign: 'center', background: '#333', padding: '10px', borderRadius: '8px' }}>
                        <div style={{ color: '#aaa', fontSize: '14px' }}>Waiting for Opponent...</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#146d75ff', margin: '10px 0', letterSpacing: '4px' }}>
                            {roomCode}
                        </div>
                        <div style={{ fontSize: '12px', color: '#777' }}>Share this code</div>
                    </div>
                ) : netStatus === 'playing' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <button disabled style={{ background: 'black', border: '1px solid #444', opacity: 0.8, cursor: 'default' }}>In Match</button>
                        {netRole === 'p1' && (
                            <button onClick={requestReset} style={{ background: GAME_THEME.BUTTON_BG, fontSize: '14px', padding: '8px' }}>Reset Match</button>
                        )}
                        {netRole !== 'p1' && (
                            <div style={{ fontSize: '12px', color: '#777', textAlign: 'center' }}>Only Host can reset</div>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <button onClick={hostGame} style={{ background: GAME_THEME.BUTTON_BG }}>Host Match</button>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <input
                                type="text"
                                placeholder="Code"
                                maxLength={4}
                                value={joinCode}
                                onChange={e => setJoinCode(e.target.value.toUpperCase())}
                                style={{
                                    width: '80px', padding: '10px', borderRadius: '4px', border: 'none',
                                    textAlign: 'center', fontWeight: 'bold', textTransform: 'uppercase'
                                }}
                            />
                            <button onClick={() => joinGame(joinCode)} style={{ flex: 1, background: GAME_THEME.BUTTON_BG }}>Join</button>
                        </div>
                    </div>
                )
            ) : (
                <button onClick={startGame}>Start Game</button>
            )}

            {mode === 'solo' && (
                <Scoreboard
                    stats={{}}
                    highScores={highScores}
                    onReset={clearScores}
                    title="Best Times"
                />
            )}
        </>
    );

    return (
        <GameLayout
            sidebarContent={sidebarContent}
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={stateRef.current.running || isCountingDown || gameState === 'waiting_next_round'}
            onQuit={() => {
                resetGameState();
                setGameState('menu');
                setShowStartModal(true);
                setIsCountingDown(false);
            }}
        >
            {isCountingDown && (
                <Countdown
                    prepMode={prepMode === 'on'}
                    onFinish={() => {
                        setIsCountingDown(false);
                        if (gameState === 'waiting_next_round') {
                            startRound();
                        } else {
                            startMatch();
                        }
                    }}
                />
            )}
            {showStartModal && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
                }}>
                    <div style={{
                        background: '#222', padding: '50px', borderRadius: '16px', textAlign: 'center',
                        border: '1px solid #444', color: '#fff', boxShadow: '0 15px 35px rgba(0,0,0,0.6)', minWidth: '350px'
                    }}>
                        <h1 style={{ marginTop: 0, color: '#4e905dff', fontSize: '2.5em', marginBottom: '10px' }}>Shrinking Target</h1>
                        <p style={{ color: '#ccc', marginBottom: '30px', fontSize: '1.1em' }}>
                            <strong>Solo:</strong> Hit 5 rounds of shrinking targets.<br />
                            <strong>Versus:</strong> Race to 5!<br />
                            Only the winner of a round advances to a smaller target.
                        </p>
                        <button
                            onClick={startGame}
                            style={{
                                padding: '16px 40px', fontSize: '20px', fontWeight: 'bold',
                                background: GAME_THEME.BUTTON_BG, color: 'white', border: '1px solid #444', borderRadius: '8px', cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            START GAME
                        </button>
                    </div>
                </div>
            )}
            {gameState === 'finished' && !showStartModal && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20
                }}>
                    <div style={{ background: '#222', padding: '40px', borderRadius: '10px', textAlign: 'center' }}>
                        <h2 style={{ color: '#4e905dff' }}>Game Over</h2>
                        {mode === 'solo' ? (
                            <p style={{ fontSize: '24px' }}>Total Time: {uiStats.lastTime.toFixed(3)}s</p>
                        ) : (
                            <div>
                                <h3 style={{ fontSize: '32px', color: uiStats.p1Score > uiStats.p2Score ? '#3498db' : (uiStats.p2Score > uiStats.p1Score ? '#e74c3c' : '#ccc') }}>
                                    {uiStats.message}
                                </h3>
                                <p>P1 Level: {uiStats.p1Score} | P2 Level: {uiStats.p2Score}</p>
                            </div>
                        )}
                        <button
                            onClick={requestReset}
                            style={{
                                marginTop: '20px', padding: '12px 30px', fontSize: '18px', fontWeight: 'bold',
                                background: GAME_THEME.BUTTON_BG, color: 'white', border: '1px solid #444', borderRadius: '6px',
                                cursor: 'pointer', transition: 'all 0.2s'
                            }}
                        >
                            {mode === 'online' ? 'Rematch' : 'Play Again'}
                        </button>
                    </div>
                </div>
            )}
        </GameLayout>
    );
};

export default ShrinkingTarget;
